import type { OnDestroy } from '@angular/core';
import { inject, Injectable } from '@angular/core';
import type {
  ActivatedRouteSnapshot,
  Navigation,
  NavigationStart,
  Params,
  ResolveFn,
} from '@angular/router';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { filter, Subject, takeUntil } from 'rxjs';

import { resetBreadcrumbState } from '@app/store/actions/breadcrumb/breadcrumb.action';
import { loadData, loadFolder, setSearch } from '@app/store/actions/file/file.action';

@Injectable({ providedIn: 'root' })
export class DataResolver implements OnDestroy {
  readonly #store = inject(Store);
  readonly #router = inject(Router);

  readonly #unsubscribe = new Subject<boolean>();
  private firstLoad = true;

  constructor() {
    this.#router.events
      .pipe(
        filter((event: NavigationStart) => event.navigationTrigger === 'popstate'),
        takeUntil(this.#unsubscribe),
      )
      .subscribe(() => {
        const currentNav: Navigation | null = this.#router.currentNavigation();
        if (!currentNav || !currentNav.previousNavigation) {
          return; // handle null in PreviousNavigation
        }
        const previousNav: Navigation = currentNav.previousNavigation;

        const previousParams: Params = previousNav.extractedUrl.queryParams;
        const currentParams: Params = currentNav.extractedUrl.queryParams;

        if (JSON.stringify(previousParams) !== JSON.stringify(currentParams)) {
          this.#store.dispatch(setSearch({ search: currentParams.search }));
          this.loadData(this.#router.routerState.root.snapshot);
        }
      });
  }

  resolve(route: ActivatedRouteSnapshot): Observable<any> | Promise<any> | any {
    const search: string = route.queryParams.search;

    if (search) {
      if (this.firstLoad) {
        this.#router.navigate(['.']);
        this.firstLoad = false;
      }

      return;
    }

    this.loadData(route);
  }

  ngOnDestroy(): void {
    this.#unsubscribe.next(false);
    this.#unsubscribe.complete();
  }

  private loadData(route: ActivatedRouteSnapshot): void {
    const folderId: string = route.params.id;
    this.firstLoad = false;

    if (folderId) {
      this.#store.dispatch(loadFolder({ id: folderId }));
    } else {
      this.#store.dispatch(resetBreadcrumbState());
    }

    this.#store.dispatch(loadData());
  }
}

export const dataResolver: ResolveFn<void> = (route: ActivatedRouteSnapshot) =>
  inject(DataResolver).resolve(route);
